<template>
  <div>
    <vue-element-loading :active="loader" spinner="bar-fade-scale" />
    <b-row>
      <b-col md="6">
        <b-button
          :variant="slotData.labelIntegration ? `primary` : `outline-primary`"
          block
          @click.stop="showLabelIntegration"
        >
          {{ slotData.labelIntegration ? `Label Integration Enabled` : `Label Integration Disabled` }}
        </b-button>
      </b-col>
      <b-col md="6" class="mt-1 mt-md-0">
        <b-button
          :variant="slotData.shortenerIntegration ? `info` : `outline-info`"
          block
          @click.stop="showShortenerIntegration"
        >
          {{ slotData.labelIntegration ? `Shortener Integration Enabled` : `Shortener Integration Disabled` }}
        </b-button>
      </b-col>
    </b-row>
    <b-row class="mt-2">
      <b-col md="12">
        <b-button
          variant="success"
          block
          :disabled="!slotData.labelIntegration"
          @click.stop="showAddDomainModal"
        >
          <feather-icon icon="PlusIcon" size="16" class="mr-1" />
          Add Existing Domain to Stock
        </b-button>
      </b-col>
    </b-row>
    <b-card>
      <template #header>
        <h5 class="mb-0">📦 Domain Stock</h5>
        <b-button
          size="sm"
          variant="outline-primary"
          @click="loadDomainStock"
          class="ml-auto"
        >
          <feather-icon icon="RefreshCwIcon" size="14" />
          Refresh
        </b-button>
      </template>

      <vue-element-loading :active="domainStockLoader" spinner="bar-fade-scale" />

      <b-table
        :items="domainStockList"
        :fields="domainStockFields"
        :per-page="domainStockPerPage"
        :current-page="domainStockCurrentPage"
        small
        striped
        hover
        responsive
        show-empty
        empty-text="No domain stock available"
      >
        <!-- Domain Column -->
        <template #cell(domain)="data">
          <strong>{{ data.value }}</strong>
        </template>

        <!-- IsUsed Column -->
        <template #cell(isUsed)="data">
          <b-badge :variant="data.value ? 'danger' : 'success'">
            {{ data.value ? 'Used' : 'Available' }}
          </b-badge>
        </template>

        <!-- IsNsSet Column -->
        <template #cell(isNsSet)="data">
          <b-badge :variant="data.value ? 'success' : 'warning'">
            {{ data.value ? '✓ Set' : '✗ Not Set' }}
          </b-badge>
        </template>

        <!-- CreatedDate Column -->
        <template #cell(createdDate)="data">
          {{ formatDate(data.value) }}
        </template>
      </b-table>

      <!-- Pagination -->
      <b-pagination
        v-model="domainStockCurrentPage"
        :total-rows="domainStockList.length"
        :per-page="domainStockPerPage"
        align="center"
        size="sm"
        class="mt-2"
      />

      <template #footer>
        <small class="text-muted">
          Total: {{ domainStockList.length }} domain(s)
          ({{ availableCount }} available, {{ usedCount }} used)
        </small>
      </template>
    </b-card>
    <hr />
    <b-form @submit.prevent="save">
      <b-form-group label="Description *" label-cols-xs="12" label-cols-lg="3">
        <b-form-input
          v-model="slotData.description"
          :state="slotData.description !== null && slotData.description !== ``"
        ></b-form-input>
      </b-form-group>
      <b-form-group label="Domain *" label-cols-xs="12" label-cols-lg="3">
        <b-form-input
          v-model="slotData.domain"
          :state="slotData.domain !== null && slotData.domain !== ``"
        ></b-form-input>
      </b-form-group>
      <div class="d-flex">
        <submit-button :disabled="loader" class="ml-auto" />
        <cancel-button :disabled="loader.form" class="ml-1" @click.stop="cancel" />
      </div>
    </b-form>

    <!-- Domain Stock Section -->
    <hr class="my-2" />
    

    <b-modal
      v-if="slotData && slotData.code"
      v-model="modal.labelIntegration"
      title="Configure Label Integration"
      size="xl"
      lazy
      centered
      hide-footer
      no-close-on-backdrop
    >
      <label-integration
        :slot-code="slotData.code"
        :client-code="slotData.clientCode"
        @onSaveSuccess="labelIntegrationSaveSuccess"
      ></label-integration>
    </b-modal>

    <b-modal
      v-if="slotData && slotData.code"
      v-model="modal.shortenerIntegration"
      title="Configure Shortener Integration"
      size="xl"
      lazy
      centered
      hide-footer
      no-close-on-backdrop
    >
      <shortener-integration :slot-code="slotData.code" :client-code="slotData.clientCode"></shortener-integration>
    </b-modal>

    <add-manual-domain-modal
      v-if="slotData && slotData.code"
      v-model="modal.addDomain"
      :slot-code="slotData.code"
      @success="addDomainSuccess"
    />
  </div>
</template>

<script>
import api from "@api";
import { mapState } from "vuex";
import LabelIntegration from "./LabelIntegration.vue";
import ShortenerIntegration from "./ShortenerIntegration.vue";
import AddManualDomainModal from "@/components/AddManualDomainModal.vue";

export default {
  name: "NawalaDetail",
  components: {
    LabelIntegration,
    ShortenerIntegration,
    AddManualDomainModal,
  },
  props: {
    slotCode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      slotData: {
        description: null,
        domain: null,
      },
      loader: false,
      domainStockList: [],
      domainStockLoader: false,
      domainStockCurrentPage: 1,
      domainStockPerPage: 5,
      modal: {
        labelIntegration: false,
        shortenerIntegration: false,
        addDomain: false,
      },
    };
  },
  computed: {
    ...mapState(`auth`, [`user`]),
    domainStockFields() {
      return [
        { key: 'domain', label: 'Domain Name', sortable: true },
        { key: 'isUsed', label: 'Status', sortable: true },
        { key: 'isNsSet', label: 'Nameservers', sortable: true },
        { key: 'createdDate', label: 'Created Date', sortable: true }
      ];
    },
    availableCount() {
      return this.domainStockList.filter(d => !d.isUsed).length;
    },
    usedCount() {
      return this.domainStockList.filter(d => d.isUsed).length;
    }
  },
  async mounted() {
    await this.loadData();
    await this.loadDomainStock();
  },
  methods: {
    async loadData() {
      this.loader = true;
      try {
        this.slotData = await api.get(`slot/detail/${this.slotCode}`);
      } catch (error) {
        this.showToast("error", error);
      } finally {
        this.loader = false;
      }
    },
    cancel() {
      this.$emit("onCancel");
    },
    async save() {
      this.loader = true;
      try {
        await api.put(`slot`, { ...this.slotData, username: this.user.email });
        this.showToast(`success`, `Slot Updated`);
        this.$emit("onSaveSuccess");
      } catch (error) {
        this.showToast("error", error);
      } finally {
        this.loader = false;
      }
    },
    showLabelIntegration() {
      this.modal.labelIntegration = true;
    },
    labelIntegrationSaveSuccess() {
      this.modal.labelIntegration = false;
    },
    showShortenerIntegration() {
      this.modal.shortenerIntegration = true;
    },
    showAddDomainModal() {
      this.modal.addDomain = true;
    },
    addDomainSuccess(result) {
      // Don't close modal here - let the child component handle auto-close
      // Just refresh the slot data
      this.$emit("onSaveSuccess");
      this.loadDomainStock();
    },
    async loadDomainStock() {
      this.domainStockLoader = true;
      try {
        this.domainStockList = await api.get(`domainstock/${this.slotCode}`);
      } catch (error) {
        this.showToast('error', error);
      } finally {
        this.domainStockLoader = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  },
};
</script>
